import React, { useContext, useState, useEffect } from "react";
import { Input, Button, Form, message, Image } from "antd";
// import ReCAPTCHA from "react-google-recaptcha";
import { Link } from "react-router-dom";
import "./LogIn.css";
import microsoftLogin from "../../assets/images/microsoft-logo.png";
import googleLogin from "../../assets/images/google-logo.png";
import HTTP from "../../services/httpService";
import { SettingContext } from "../../routes";
import { useNavigate } from "react-router-dom";
import { encrypt } from "../../services/util";
import { queryClient } from "../../App";
import { useAuth0 } from "@auth0/auth0-react";
import { LoginOutlined } from "@ant-design/icons";
import {
    GoogleReCaptchaProvider,
    useGoogleReCaptcha,
} from "react-google-recaptcha-v3";

// ── Auth0 button components ───────────────────────────
function GoogleLoginButton() {
    const { loginWithPopup, isAuthenticated, logout } = useAuth0();
    return (
        <Button
            className="login-social-btn"
            icon={<Image src={googleLogin} width={18} preview={false} />}
            onClick={async () => {
                if (isAuthenticated)
                    await logout({
                        logoutParams: {
                            silent: true,
                            returnTo: window.location.origin,
                            federated: true,
                        },
                    });
                await loginWithPopup(
                    {
                        authorizationParams: {
                            connection: "google-oauth2",
                            prompt: "login",
                        },
                    },
                    { timeoutInSeconds: 120 },
                );
            }}
        >
            Continue with Google
        </Button>
    );
}

function MicrosoftLoginButton() {
    const { loginWithPopup, isAuthenticated, logout } = useAuth0();
    return (
        <Button
            className="login-social-btn"
            icon={<Image src={microsoftLogin} width={18} preview={false} />}
            onClick={async () => {
                if (isAuthenticated)
                    await logout({
                        logoutParams: {
                            silent: true,
                            returnTo: window.location.origin,
                            federated: true,
                        },
                    });
                await loginWithPopup(
                    {
                        authorizationParams: {
                            connection: "AzureADv2",
                            prompt: "login",
                            scope: "openid profile email",
                        },
                    },
                    { timeoutInSeconds: 120 },
                );
            }}
        >
            Continue with Microsoft
        </Button>
    );
}

function SSOLoginButton() {
    const { loginWithPopup, isAuthenticated, logout } = useAuth0();
    return (
        <Button
            className="login-social-btn"
            icon={<LoginOutlined style={{ color: "#f49209", fontSize: 16 }} />}
            onClick={async () => {
                if (isAuthenticated)
                    await logout({
                        logoutParams: {
                            silent: true,
                            returnTo: window.location.origin,
                            federated: true,
                        },
                    });
                await loginWithPopup(
                    {
                        authorizationParams: {
                            connection: "Aavgo-SSO-via-Auth0",
                            prompt: "login",
                            scope: "openid profile email",
                        },
                    },
                    { timeoutInSeconds: 120 },
                );
            }}
        >
            Continue with SSO
        </Button>
    );
}

function LogIn() {
    const [loginMessage, setLoginMessage] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { loggedIn, setLoggedIn } = useContext(SettingContext);
    const { isLoading, isAuthenticated, user, error, logout } = useAuth0();
    const { executeRecaptcha } = useGoogleReCaptcha();
    const navigate = useNavigate();

    useEffect(() => {
        if (loggedIn) {
            queryClient.clear();
            navigate("/");
        }
    });

    useEffect(async () => {
        if (isLoading) return;
        if (isAuthenticated && user) {
            if (user.email) logInWithAuth0(user);
            else setLoginMessage("Email not found in Auth0 user profile");
        } else if (error) {
            setLoginMessage(error.message);
        }
    }, [isAuthenticated, isLoading, user, error]);

    const logInWithAuth0 = async (auth0User) => {
        if (!auth0User?.email) return;
        const {
            data: response,
            success,
            message: msg,
        } = await HTTP("/users/login", {
            email: auth0User.email,
            isAdfsUser: false,
            is_social_login: true,
            first_name: auth0User.given_name,
            last_name: auth0User.family_name,
            social_platform: auth0User.sub.includes("google") ? 1 : 2,
            api_version: "v3",
        });
        if (!success) {
            await logout({
                logoutParams: {
                    silent: true,
                    returnTo: window.location.origin,
                },
            });
            setLoginMessage(msg);
        }
        if (response?.token) setLoggedIn(true);
    };

    const isLocal = () =>
        window.location.hostname === "localhost" ||
        window.location.hostname.includes("192.168");

    const needToBypassCaptcha = ({ email }) =>
        email.length === 7 && email.toUpperCase() === "ABQ0001";

    async function submit(values) {
        setIsSubmitting(true);
        try {
            let captchaValue = null;
            console.log(
                "Here checking conditions",
                isLocal(),
                needToBypassCaptcha(values),
            );
            if (!isLocal() && !needToBypassCaptcha(values)) {
                if (!executeRecaptcha) {
                    console.error("reCAPTCHA not loaded");
                    setLoginMessage(
                        "reCAPTCHA is not ready. Please try again.",
                    );
                    return;
                }
                captchaValue = await executeRecaptcha("submit");
                console.log("captchaValue", captchaValue);
            }

            setLoginMessage("");
            values.password = encrypt(values.password);
            const {
                data: response,
                success,
                message: msg,
            } = await HTTP("/users/login", {
                ...values,
                google_captcha: captchaValue,
                isAdfsUser: false,
                api_version: "v3",
            });
            if (!success) setLoginMessage(msg);
            if (response?.token) setLoggedIn(true);
        } catch (err) {
            setLoginMessage(String(err));
        } finally {
            setIsSubmitting(false);
        }
    }

    return (
        <div className="login-page login-page-simple">
            <div className="login-layout">
                <div className="login-right">
                    <h2 className="login-form-title">Welcome back</h2>
                    <p className="login-form-subtitle">
                        Sign in to your account to continue
                    </p>

                    {/* Error message */}
                    {loginMessage && (
                        <div className="login-error">
                            <span>⚠</span>
                            <span>{loginMessage}</span>
                        </div>
                    )}

                    {/* Credential form */}
                    <Form layout="vertical" onFinish={submit}>
                        <Form.Item
                            name="email"
                            rules={[
                                {
                                    required: true,
                                    whitespace: true,
                                    message: "Please enter your username.",
                                },
                            ]}
                        >
                            <Input
                                size="large"
                                placeholder="Username or email"
                            />
                        </Form.Item>

                        <Form.Item
                            name="password"
                            rules={[
                                {
                                    required: true,
                                    whitespace: true,
                                    message: "Please enter your password.",
                                },
                            ]}
                        >
                            <Input.Password
                                size="large"
                                placeholder="Password"
                            />
                        </Form.Item>

                        <div className="login-forgot">
                            <Link to="/forgot-password">Forgot password?</Link>
                        </div>

                        <Button
                            type="primary"
                            htmlType="submit"
                            className="login-submit-btn"
                            loading={isSubmitting}
                            disabled={isSubmitting}
                        >
                            Sign In
                        </Button>
                    </Form>

                    {/* Divider */}
                    <div className="login-divider">
                        <div className="login-divider-line" />
                        <span className="login-divider-text">
                            or continue with
                        </span>
                        <div className="login-divider-line" />
                    </div>

                    {/* Social / SSO */}
                    <div className="login-social-buttons">
                        <MicrosoftLoginButton />
                        <GoogleLoginButton />
                        {/* <SSOLoginButton /> */}
                    </div>
                </div>
            </div>
        </div>
    );
}

const LogInWrapper = () => {
    return (
        <GoogleReCaptchaProvider
            reCaptchaKey={process.env.REACT_APP_SITEKEY_V3}
            // scriptProps={{
            //     async: true,
            //     defer: true,
            //     appendTo: "head",
            // }}
        >
            <LogIn />
        </GoogleReCaptchaProvider>
    );
};

export { LogInWrapper as LogIn };
